import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Icon, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { CreateHeaders, textHeader } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';
import WebkuaTable from '../webkua-table/webkua-table';
import { ReconciliationLink, ReconciliationLinks, salesforceRecordUrl, webkuaContractUrl } from './reconciliation-links';
import {
  contractDate as date, contractFilters, contractFindings, contractIssueMessages, contractNextCheck,
  contractPresence, contractRank, contractValue, matchesContractFilter, renewalLag,
} from './contract-reconciliation-presentation';
import type { ContractFilter, ContractText } from './contract-reconciliation-presentation';
import { ReconciliationComparisonFilters } from './reconciliation-filter-select';
import { ReconciliationDetailsLayout, ReconciliationSourcePanel, ReconciliationSourcePanels } from './reconciliation-details-layout';
import { useContractSyncControls } from './contract-sync-controls';

export interface ContractSource {
  id: string | null;
  companyNumber: string | null;
  companyName: string | null;
  accountId: string | null;
  contractNumber: string | null;
  salesforceContractNumber: string | null;
  isCurrent: boolean | null;
  status: string | null;
  startDate: string | null;
  nextRenewalDate: string | null;
  terminationDate: string | null;
  maintenanceStopDate: string | null;
  currency: string | null;
  invoiceInterval: number | null;
  contractResponsibleEmail: string | null;
  invoicedCompanyNumber: string | null;
  hasContractInfo: boolean;
  hasInvoiceInfo: boolean;
}

export interface ContractReconciliationRow {
  salesforceContractNumber: string | null;
  kind: string;
  issues: string[];
  warnings: string[];
  sourceDiagnostics?: Array<{ system: 'SF' | 'WK'; sourceId: string | null; issues: string[] }>;
  differences: Array<{ field: string; salesforceValue: string | null; webkuaValue: string | null }>;
  salesforceContracts: ContractSource[];
  webkuaContracts: ContractSource[];
}

export interface ContractReconciliationResult {
  salesforceContracts?: ContractSource[];
  comparedAt: string;
  comparedGroupCount: number;
  inSyncGroupCount: number;
  rows: ContractReconciliationRow[];
}

const sourceFields = ['isCurrent', 'startDate', 'nextRenewalDate', 'terminationDate', 'currency',
  'invoiceInterval', 'contractResponsibleEmail', 'invoicedCompanyNumber'] as const;
const exportFields = [...sourceFields, 'companyNumber', 'maintenanceStopDate', 'status'] as const;
const customerLabel = (source: ContractSource) => source.companyName
  ? `${source.companyName} (${source.companyNumber || '-'})` : source.companyNumber || '-';

function renewalAssessment(row: ContractReconciliationRow, text: (key: string) => string) {
  if (row.salesforceContracts.length !== 1 || row.webkuaContracts.length !== 1
    || !row.salesforceContracts[0].nextRenewalDate || !row.webkuaContracts[0].nextRenewalDate) {
    return text('renewalUnresolved');
  }
  if (date(row.salesforceContracts[0].nextRenewalDate) !== date(row.webkuaContracts[0].nextRenewalDate)) {
    const lag = renewalLag(row);
    return text(lag?.system === 'WK' ? 'notRenewedInWebkua' : lag?.system === 'SF' ? 'notRenewedInSalesforce' : 'differentRenewalDates');
  }
  return text('matchingRenewalDates');
}

function ContractRenewalDates({ sources, system, comparedAt }: {
  sources: ContractSource[]; system: 'SF' | 'WK'; comparedAt: string;
}) {
  const { t } = useTranslation();
  const warning = t(`salesForceContractReconciliation.warnings.${system === 'SF'
    ? 'SalesforceRenewalDatePassed' : 'WebkuaRenewalDatePassed'}`);
  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
    {sources.length === 0 ? '-' : sources.map((source, index) => {
      const passed = source.nextRenewalDate != null
        && date(source.nextRenewalDate) < date(comparedAt);
      return <Box key={index} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <span>{date(source.nextRenewalDate)}</span>
        {passed && <Tooltip title={warning}>
          <Icon role="img" aria-hidden={false} aria-label={warning} tabIndex={0}
            sx={{ fontSize: 18, color: 'warning.main' }}>warning_amber</Icon>
        </Tooltip>}
      </Box>;
    })}
  </Box>;
}

function ContractCustomerLinks({ row }: { row: ContractReconciliationRow }) {
  const customers = row.salesforceContracts.map((source) => ({
    label: customerLabel(source),
    href: source.accountId ? salesforceRecordUrl('Account', source.accountId) : null,
  }));
  for (const source of row.webkuaContracts) {
    const label = customerLabel(source);
    if (!customers.some((customer) => customer.label === label)) customers.push({ label, href: null });
  }
  return <ReconciliationLinks links={customers.filter((customer, index) =>
    customers.findIndex((other) => other.label === customer.label && other.href === customer.href) === index)} />;
}

function ContractSourceLinks({ source, system }: { source: ContractSource; system: 'SF' | 'WK' }) {
  const { t } = useTranslation();
  const company = customerLabel(source);
  return <Box>
    <div><ReconciliationLink label={source.contractNumber || String(t('salesForceContractReconciliation.contract'))}
      href={system === 'SF' ? source.id ? salesforceRecordUrl('Contract', source.id) : null
        : source.companyNumber && source.contractNumber ? webkuaContractUrl(source.companyNumber, source.contractNumber) : null} /></div>
    <Typography variant="caption" component="div">
      <ReconciliationLink label={company} href={system === 'SF'
        ? source.accountId ? salesforceRecordUrl('Account', source.accountId) : null
        : source.companyNumber ? `/customers/${encodeURIComponent(source.companyNumber)}` : null} />
    </Typography>
  </Box>;
}

export function ContractReconciliationDetails({ data }: { data?: { source: ContractReconciliationRow } }) {
  const { t } = useTranslation();
  if (!data) return null;
  const row = data.source;
  const text: ContractText = (key, options) => t(`salesForceContractReconciliation.${key}`,
    { ...options, returnObjects: false, returnDetails: false });
  const findings = contractFindings(row, text);
  const issues = contractIssueMessages(row, text);
  return <ReconciliationDetailsLayout testId="contract-details" title={findings[0] ?? text('noFindingDetails')}>
    <Typography variant="body2">{text('sourcePresence')}: {contractPresence(row, text)}</Typography>
    {matchesContractFilter(row, 'linkage') && <Alert severity="info">{text('checks.customerLink')}</Alert>}
    {issues.length > 0 && <Alert severity="warning">
      {issues.map((issue) => <div key={issue}>{issue}</div>)}
      <Typography variant="body2">{text('checks.sourceData')}</Typography>
    </Alert>}
    {row.warnings.length > 0 && <Alert severity="warning" data-testid="contract-warnings">
      {row.warnings.map((warning) => <div key={warning}>{text(`warnings.${warning}`)}</div>)}
    </Alert>}
    {row.differences.length > 0 && <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="subtitle2">{text('differences')}</Typography>
      <Table size="small" data-testid="contract-differences">
      <TableHead><TableRow>{['field', 'sfValue', 'wkValue', 'nextCheck'].map((key) =>
        <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
      <TableBody>{row.differences.map((difference) => <TableRow key={difference.field}>
        <TableCell>{text(`fields.${difference.field}`)}</TableCell>
        <TableCell>{contractValue(difference.field, difference.salesforceValue, text)}</TableCell>
        <TableCell>{contractValue(difference.field, difference.webkuaValue, text)}</TableCell>
        <TableCell>{contractNextCheck(difference.field, text)}</TableCell>
      </TableRow>)}</TableBody>
      </Table>
    </Box>}
    <ReconciliationSourcePanels>
      {(['SF', 'WK'] as const).map((system) => {
        const sources = system === 'SF' ? row.salesforceContracts : row.webkuaContracts;
        return <ReconciliationSourcePanel key={system}
          name={system === 'SF' ? 'Salesforce' : 'WebKUA'} testId={`contract-${system}-sources`}>
          {sources.length === 0 ? <Typography>{text('noSource')}</Typography>
            : <Table size="small">
              <TableHead><TableRow><TableCell>{text('contract')}</TableCell>
                {sourceFields.map((field) => <TableCell key={field}>{text(`fields.${field}`)}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>{sources.map((source, index) => <TableRow key={index}>
                <TableCell><ContractSourceLinks source={source} system={system} /></TableCell>
                {sourceFields.map((field) => <TableCell key={field}>
                  {field === 'isCurrent'
                    ? <>{source.isCurrent == null ? text('unknown') : text(source.isCurrent ? 'current' : 'ended')}
                      {source.status && <Typography variant="caption" component="div">{source.status}</Typography>}</>
                    : typeof source[field] === 'string' && field.endsWith('Date')
                      ? date(source[field]) : source[field] ?? '-'}
                  {field === 'terminationDate' && source.maintenanceStopDate && <Typography variant="caption" component="div">
                    {text('fields.maintenanceStopDate')}: {date(source.maintenanceStopDate)}
                  </Typography>}
                </TableCell>)}
              </TableRow>)}</TableBody>
            </Table>}
        </ReconciliationSourcePanel>;
      })}
    </ReconciliationSourcePanels>
  </ReconciliationDetailsLayout>;
}

export function SalesForceContractReconciliation({ result, onRefresh }: {
  result: ContractReconciliationResult; onRefresh?: () => void;
}) {
  const { t } = useTranslation();
  const text: ContractText = (key, options) => t(`salesForceContractReconciliation.${key}`,
    { ...options, returnObjects: false, returnDetails: false });
  const [filter, setFilter] = useState<ContractFilter | ''>('');
  const [onlySyncable, setOnlySyncable] = useState(false);
  const sync = useContractSyncControls(result.comparedAt, onRefresh);
  const absent = (source: ContractReconciliationRow) => text(source.kind === 'MissingKey' ? 'notCompared' : 'notFound');
  const rows = result.rows.map((source) => {
    const findings = contractFindings(source, text);
    return {
    source,
    sfLinkKey: source.webkuaContracts.map((contract) => contract.salesforceContractNumber ?? '-').join(', '),
    sfContract: source.salesforceContracts.map((contract) => contract.contractNumber ?? '-').join(', ') || absent(source),
    wkContract: source.webkuaContracts.map((contract) => contract.contractNumber ?? '-').join(', ') || absent(source),
    company: [...new Set([...source.salesforceContracts, ...source.webkuaContracts]
      .map(customerLabel))].join(' / '),
    presence: contractPresence(source, text),
    findings,
    allFindings: findings.join('; '),
    status: findings[0] ?? text('noFindingDetails'),
    comparisonKind: text(`kinds.${source.kind}`),
    changedFields: source.differences.map((difference) => text(`fields.${difference.field}`)).join(', '),
    renewalAssessment: renewalAssessment(source, text),
    issues: contractIssueMessages(source, text).join(', '),
    warnings: source.warnings.map((warning) => text(`warnings.${warning}`)).join(', '),
    differences: source.differences.map((difference) =>
      `${text(`fields.${difference.field}`)}: SF ${difference.salesforceValue ?? '-'} / WK ${difference.webkuaValue ?? '-'}`).join('; '),
    ...Object.fromEntries(exportFields.flatMap((field) => (['SF', 'WK'] as const).map((system) => [
      `${system}_${field}`, (system === 'SF' ? source.salesforceContracts : source.webkuaContracts)
        .map((contract) => contract[field] == null ? '-' : typeof contract[field] === 'string' && field.endsWith('Date')
          ? contract[field].slice(0, 10) : String(contract[field])).join(', '),
    ]))),
    };
  }).sort((a, b) => contractRank(a.source) - contractRank(b.source));
  const displayedRows = rows.filter((row) => (!onlySyncable || sync.canSync(row.source)) &&
    (!filter || matchesContractFilter(row.source, filter)));
  type Row = typeof rows[number];
  return <Box>
    <Alert severity="info">{t('salesForceContractReconciliation.counts', {
      inSync: result.inSyncGroupCount, compared: result.comparedGroupCount,
    })}</Alert>
    {rows.length === 0 && <Alert severity="success">{text('noDifferences')}</Alert>}
    <ReconciliationComparisonFilters rows={result.rows} filters={contractFilters}
      filter={filter} onFilterChange={setFilter} onlySyncable={onlySyncable} onSyncableChange={setOnlySyncable}
      matches={matchesContractFilter} canSync={sync.canSync} filterLabel={(value) => text(`filters.${value}`)} />
    <WebkuaTable data={displayedRows} expandComponent={ContractReconciliationDetails}
      customComponent={<Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="body2">{text('filteredRows', { shown: displayedRows.length, total: rows.length })}</Typography>
        {(filter || onlySyncable) && <Button size="small" onClick={() => {
          setFilter(''); setOnlySyncable(false);
        }}>{text('clearFilters')}</Button>}
      </Stack>}
      exportColumnKeys={['company', 'sfContract', 'wkContract', 'sfLinkKey', 'presence', 'status', 'allFindings', 'comparisonKind',
        'changedFields', 'renewalAssessment',
        'issues', 'warnings', 'differences',
        ...exportFields.flatMap((field) => [`SF_${field}`, `WK_${field}`])]}
      headers={CreateHeaders([
        textHeader('company', text('company'), undefined, {
          renderer: 'agGroupCellRenderer',
          rendererParams: {
            innerRenderer: ({ data }: { data?: Row }) => data ? <ContractCustomerLinks row={data.source} /> : null,
          },
        }),
        textHeader('sfContract', text('sfContract'), undefined, {
          renderer: ({ data }: { data?: Row }) => <Box>{data && (data.source.salesforceContracts.length === 0
            ? absent(data.source) : data.source.salesforceContracts.map((contract, index) =>
              <ContractSourceLinks key={index} source={contract} system="SF" />))}</Box>,
        }),
        textHeader('wkContract', text('wkContract'), undefined, {
          renderer: ({ data }: { data?: Row }) => <Box>{data && (data.source.webkuaContracts.length === 0
            ? absent(data.source) : data.source.webkuaContracts.map((contract, index) =>
              <ContractSourceLinks key={index} source={contract} system="WK" />))}</Box>,
        }),
        textHeader('presence', text('sourcePresence'), undefined, { hide: true }),
        textHeader('SF_status', text('fields.status')),
        textHeader('SF_nextRenewalDate', `SF: ${text('fields.nextRenewalDate')}`, undefined, {
          renderer: ({ data }: { data?: Row }) => data ? <ContractRenewalDates
            sources={data.source.salesforceContracts} system="SF" comparedAt={result.comparedAt} /> : null,
        }),
        textHeader('WK_nextRenewalDate', `WK: ${text('fields.nextRenewalDate')}`, undefined, {
          renderer: ({ data }: { data?: Row }) => data ? <ContractRenewalDates
            sources={data.source.webkuaContracts} system="WK" comparedAt={result.comparedAt} /> : null,
        }),
        textHeader('renewalAssessment', text('renewalAssessment')),
        textHeader('status', text('mainFinding'), undefined, {
          renderer: ({ data }: { data?: Row }) => data ? <Box>
            <div>{data.status}</div>
            {data.findings.length > 1 && <Typography variant="caption" component="div">
              {text('moreFindings', { count: data.findings.length - 1 })}
            </Typography>}
          </Box> : null,
        }),
        textHeader('changedFields', text('changedFields'), undefined, { hide: true }),
        textHeader('sfLinkKey', text('sfLinkKey'), undefined, { hide: true }),
        textHeader('comparisonKind', text('comparisonKind'), undefined, { hide: true }),
        textHeader('allFindings', text('allFindings'), undefined, { hide: true }),
        textHeader('issues', text('issuesTitle'), undefined, { hide: true }),
        textHeader('warnings', text('warningsTitle'), undefined, { hide: true }),
        textHeader('differences', text('differences'), undefined, { hide: true }),
        ...exportFields.filter((field) => field !== 'nextRenewalDate').flatMap((field) =>
          (field === 'status' ? ['WK'] : ['SF', 'WK']).map((system) =>
          textHeader(`${system}_${field}`, `${system}: ${text(`fields.${field}`)}`, undefined, { hide: true }))),
        textHeader('syncAction', text('sync.column'), undefined, {
          renderer: ({ data }: { data?: Row }) => data ? sync.action(data.source) : null,
        }),
      ]).map((header) => header.field === 'syncAction'
        ? { ...header, pinned: 'right', lockPinned: true, suppressMovable: true,
          width: 260, minWidth: 240, flex: 0, suppressSizeToFit: true,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } }
        : ['SF_nextRenewalDate', 'WK_nextRenewalDate'].includes(header.field)
        ? { ...header, width: 150, minWidth: 140, flex: 0, suppressSizeToFit: true,
          wrapHeaderText: true, autoHeaderHeight: true }
        : header)}
    />
    <Accordion disableGutters elevation={0} sx={{ mt: 2 }} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <Typography variant="body2">{text('about')}</Typography>
      </AccordionSummary>
      <AccordionDetails><Stack spacing={1}>
        {['scope', 'dateRules', 'renewalRules', 'billingMappings', 'findingRules', 'orderLimitations'].map((key) =>
          <Typography key={key} variant="body2">{text(key)}</Typography>)}
      </Stack></AccordionDetails>
    </Accordion>
    {sync.dialog}
  </Box>;
}
